/*   
    Developers: Sakshi Bhise     
    Script: api.ts     
    Date: 06/18/2025    
    Project Name: AskHR     
    Team: DSET     
  
    Purpose of the Script:   
    This script defines all API call functions that connect the AskHR frontend to the backend services.   
    It manages operations such as starting chat conversations, handling chat history, file downloads, user authentication tokens, email ticketing, feedback submission, and fetching application settings.  
    Centralizing API calls in one module helps maintain clear organization, reusability, and cleaner business logic throughout the application.  
    Thorough error handling, toast notifications, and token refresh logic enhance user experience and security.  
*/

// Imports required data models and utilities  
import {
    UserInfo,
    ConversationRequest,
    Conversation,
    ChatMessage,
    SQLDBHealth,
    SQLDBStatus
} from "./models";
import { chatHistorySampleData } from "../constants/chatHistory";
import { SubmitTicketPayload } from "@teva/ui";

// Get base url for backend. Only for development mode!
const backend_url = import.meta.env.VITE_BACKEND_URL || ""


// Function to send an email (e.g., to ServiceNow or similar ticketing system)  
export async function sendEmail(ticket: SubmitTicketPayload, gid: any, country: any, token: any, impersonatingUserCountry:any) {

    try {
        const formData = new FormData();
        formData.append("subject", ticket.title);
        formData.append("body", ticket.description);
        formData.append("gid", gid);
        formData.append("country", country)
        formData.append("impersonated_user_country", impersonatingUserCountry)

        // If there are attachments, append each file  
        if (ticket.attachments && ticket.attachments.length > 0) {
            for (const file of ticket.attachments) {
                // All under the same field name 'attachments'  
                formData.append("attachments", file, file.name);
            }
        }

        const response = await fetch(`${backend_url}/send-email`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`
            },
            body: formData
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error("Error sending email:", error);
        return null;
    }
}

/** Small util to decode a JWT without bringing in a library */
function parseJwt(token: string): { exp: number } {
    const [, payload] = token.split(".");
    return JSON.parse(atob(payload));
}

// Fetch user information (token, profile, etc.)  
export async function getUserInfo(token: any): Promise<any> {
    try {

        const userInfo = parseJwt(token);
        return userInfo;
    } catch (error) {
        return [];
    }
}

/*------------------- Chat History API Start -------------------*/

// Returns initial chat history (used for local mock/testing).  
export const fetchChatHistoryInit = (): Conversation[] | null => {
    // Replace with API if required; currently using mock data  
    // return null;  
    return chatHistorySampleData;
}
 
// Fetches a paginated list of conversation metadata with messages.  
export const historyList = async (offset = 0, global_id: any, token: any, count = 25): Promise<Conversation[] | null> => {
    const response = await fetch(`${backend_url}/history/list?offset=${offset}&count=${count}`, {
        method: "GET", headers: {
            Authorization: `Bearer ${token}`, // Add the token to the Authorization header  
            "Content-Type": "application/json", // Optional: Specify the content type  
        },
    }).then(async (res) => {
        const payload = await res.json();
        if (!Array.isArray(payload)) {
            return null;
        }
        // Fetch all messages for each conversation  
        const conversations: Conversation[] = await Promise.all(payload.map(async (conv: any) => {
            let convMessages: ChatMessage[] = [];
            convMessages = await historyRead(conv.id, conv.title, global_id, token)
                .then((res) => { return res })
                .catch((err) => { return [] })
            const conversation: Conversation = {
                id: conv.id,
                title: conv.title,
                date: conv.createdAt,
                messages: convMessages
            };
            return conversation;
        }));
        return conversations;
    }).catch((err) => {
        return err
    })
    return response
}

// Reads the messages for a single conversation.  
export const historyRead = async (convId: string, title: string, global_id: any, token: any): Promise<ChatMessage[]> => {
    const response = await fetch(`${backend_url}/history/read`, {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId,
            conversation_title: title,
            global_id: global_id
        }),
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
        },
    })
        .then(async (res) => {
            if (!res) { return [] }
            const payload = await res.json();
            let messages: ChatMessage[] = [];
            if (payload?.messages) {
                payload.messages.forEach((msg: any) => {
                    const message: ChatMessage = {
                        id: msg.id,
                        role: msg.role,
                        date: msg.createdAt,
                        content: msg.content,
                        feedback: msg.feedback ?? undefined // check if msg.feedback exists then set to msg.feedback or set to undefined 
                    }
                    messages.push(message)
                });
            }
            return messages;
        }).catch((err) => {
            return []
        })
    return response
}

// Creates a new or continues an existing conversation with history enabled.  
export const historyGenerate = async (
    options: ConversationRequest,
    abortSignal: AbortSignal,
    country: string,
    token: any,
    impersonatedUser: any,
    global_id: any,
    convId?: string,
    impersonated_user_country?: string
): Promise<Response> => {
    let body;
    if (convId) {
        body = JSON.stringify({
            conversation_id: convId,
            user_permission: options.user_permission,
            messages: options.messages,
            country: country,
            impersonated_user: impersonatedUser,
            global_id: global_id,
            impersonated_user_country
        })
    } else {
        body = JSON.stringify({
            messages: options.messages,
            user_permission: options.user_permission,
            country: country,
            impersonated_user: impersonatedUser,
            global_id: global_id,
            impersonated_user_country
        })
    }
    const response = await fetch(`${backend_url}/history/generate`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`

        },
        body: body,
        signal: abortSignal
    }).then((res) => {
        return res
    })
        .catch((err) => {
            return new Response;
        })
    return response
}

// Updates a conversation (by chat message list and title)  
export const historyUpdate = async (
    messages: ChatMessage[],
    country: any,
    convId: string,
    title: string,
    impersonated_user: any,
    global_id: any,
    token: any,
    impersonated_user_country:any
): Promise<Response> => {
    const response = await fetch(`${backend_url}/history/update`, {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId,
            country: country,
            conversation_title: title,
            messages: messages,
            impersonated_user: impersonated_user,
            global_id: global_id,
            impersonated_user_country
        }),
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
        },
    }).then(async (res) => {
        return res
    })
        .catch((err) => {
            let errRes: Response = {
                ...new Response,
                ok: false,
                status: 500,  // ERROR 500 - Internal server error
            }
            return errRes;
        })
    return response
}

// Delete a specific conversation from the user's history.  
export const historyDelete = async (convId: string, title: string, country: any, global_id: any, token: any, impersonated_user_country:any): Promise<Response> => {
    const response = await fetch(`${backend_url}/history/delete`, {
        method: "DELETE",
        body: JSON.stringify({
            conversation_id: convId,
            country: country,
            conversation_title: title,
            global_id: global_id,
            impersonated_user_country
        }),
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
        },
    })
        .then((res) => { return res })
        .catch((err) => {
            let errRes: Response = {
                ...new Response,
                ok: false,
                status: 500,  // ERROR 500 - Internal server error
            }
            return errRes;
        })
    return response;
}

// Delete all conversations from the user's history.  
export const historyDeleteAll = async (token: any): Promise<Response> => {
    const response = await fetch(`${backend_url}/history/delete_all`, {
        method: "DELETE",
        body: JSON.stringify({}),
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
        },
    })
        .then((res) => { return res })
        .catch((err) => {
            let errRes: Response = {
                ...new Response,
                ok: false,
                status: 500,  // ERROR 500 - Internal server error
            }
            return errRes;
        })
    return response;
}

// Rename a conversation title in history.  
export const historyRename = async (convId: string, title: string, country: any, global_id: any, token: any, impersonated_user_country:any): Promise<Response> => {
    const response = await fetch(`${backend_url}/history/rename`, {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId,
            country: country,
            title: title,
            global_id: global_id,
            impersonated_user_country
        }),
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
        },
    })
        .then((res) => { return res })
        .catch((err) => {
            let errRes: Response = {
                ...new Response,
                ok: false,
                status: 500,
            }
            return errRes;
        })
    return response;
}

// Ensure SQL DB (history database) is available and return status.  
export const historyEnsure = async (token: any): Promise<SQLDBHealth> => {
    const response = await fetch(`${backend_url}/history/ensure`, {
        method: "GET", headers: {
            Authorization: `Bearer ${token}`, // Add the token to the Authorization header  

        },
    })
        .then(async res => {
            let respJson = await res.json();
            let formattedResponse;
            if (respJson.message) {
                formattedResponse = SQLDBStatus.Working
            } else {
                if (res.status === 500) {  // ERROR 500 - Internal server error
                    formattedResponse = SQLDBStatus.NotWorking
                } else if (res.status === 401) {  // ERROR 401 - Unauthorized
                    formattedResponse = SQLDBStatus.InvalidCredentials
                } else if (res.status === 422) {  // ERROR 422 - Unprocessable entity
                    formattedResponse = respJson.error
                } else {
                    formattedResponse = SQLDBStatus.NotConfigured
                }
            }
            if (!res.ok) {
                return {
                    sqlDB: false,
                    status: formattedResponse
                }
            } else {
                return {
                    sqlDB: respJson.message?.includes("Enabled"),
                    status: formattedResponse
                }
            }
        })
        .catch((err) => {
            return {
                sqlDB: false,
                status: err
            }
        })
    return response;
}

// Submit or update user feedback on a single chat message.  
export const historyMessageFeedback = async (
    messageId: string,
    country: string,
    feedback: string,
    token: any,
    global_id: any,
    detailed_feedback?: string,
    consent_flag?: boolean,
    impersonated_user_country?: string
): Promise<Response> => {
    const response = await fetch(`${backend_url}/history/message_feedback`, {
        method: "POST",
        body: JSON.stringify({
            message_id: messageId,
            country: country,
            message_feedback: feedback,
            global_id: global_id,
            feedback_description: detailed_feedback,
            consent_flag: consent_flag ? 1 : 0,
            impersonated_user_country
        }),
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
        },
    })
        .then((res) => { return res })
        .catch((err) => {
            let errRes: Response = {
                ...new Response,
                ok: false,
                status: 500,  // ERROR 500 - Internal server error
            }
            return errRes;
        })
    return response;
}
/*------------------- Chat History API End -------------------*/

// API call to obtain frontend settings like logo, authentication, etc.  
export const frontendSettings = async (token: any): Promise<Response | null> => {
    const response = await fetch(`${backend_url}/frontend_settings`, {
        method: "GET", headers: {
            Authorization: `Bearer ${token}`, // Add the token to the Authorization header  
        },
    }).then((res) => {
        return res.json()
    }).catch((err) => {
        return null
    })
    return response
}

// eventLogger API call 
export const logEvent = async (eventData: any, country: string, global_id: any, token: any,  impUserCountry?:string, eventType: string = "INFO",) => {
    try {
        await fetch(`${backend_url}/log-event`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json', Authorization: `Bearer ${token}`, // Add the token to the Authorization header            
            },
            body: JSON.stringify({ eventData, country, global_id, eventType, impUserCountry }),
        });
    } catch (e) {
        console.error('Event logging failed:', e);
    }
}

//Impersonate User API call
export const impersonateUser = async (username: string, country: any, global_id: any, token: any, impersonated_user_country:string|null = null): Promise<Response> => {
    const response = await fetch(`${backend_url}/impersonate`, {
        method: "POST",
        body: JSON.stringify({ username, country, global_id, impersonated_user_country }),
        headers: {
            "Content-Type": "application/json", Authorization: `Bearer ${token}`
        },
    })
        .then((res) => { return res })
        .catch((err) => {
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
            }
            return errRes;
        });
    return response;
};

// To Accept Agreement accepted at timestamp.
export const acceptUserConsent = async (token: any, disclaimer:string) => {
    const response = await fetch(`${backend_url}/accept-terms`, {
        method: "PUT",
        body: disclaimer,
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return response;
}

// Generate Description for a conversation
export const generateDescription = async (token: any, messages: any, userCountry: any, userGId: any, impersonated_user_country:string | null) => {
    const response = await fetch(`${backend_url}/generate-description`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            "conversation_messages": messages,
            "country": userCountry,
            "global_id": userGId,
            impersonated_user_country
        })
    })
    return response;
}

// Update default language for user
export const updateLanguage = async (token: any, payload: any) => {
    const response = await fetch(`${backend_url}/update-language`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    return response
}

// Update last login for a user
export const updateLastLogin = async (token: any) => {
    const response = await fetch(`${backend_url}/update-last-login`, {
        headers: {
            'Authorization': `Bearer ${token}`
        },
    })
    return response;
}

// Get User details from backend
export const getUserDetails = async (token: any) => {
    const response = await fetch(`${backend_url}/get-user-details`, {
        headers: { Authorization: `Bearer ${token}` }, // Use token in headers  
    });

    return response
}

// Get languages for user from backend
export const getLanguages = async (token:any) => {
    const response = await fetch(`${backend_url}/languages`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return response;
}

// Get backend token from teams token
export const getAuthority = async(teamsToken:any)=>{
    const response = await fetch(`${backend_url}/get-authority`, {
              method: "GET",
              headers: { Authorization: `Bearer ${teamsToken}` },
            });
            return response;
}